import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import MessageBubble from './MessageBubble';
import EmojiPicker from './EmojiPicker';

const MAX_FILE_MB = parseInt(process.env.REACT_APP_MAX_FILE_MB || '40');

const avatarColors = ['bg-violet-600', 'bg-indigo-600', 'bg-sky-600', 'bg-teal-600', 'bg-emerald-600', 'bg-pink-600', 'bg-rose-600'];
function avatarColor(username) {
  return username ? avatarColors[username.charCodeAt(0) % avatarColors.length] : 'bg-slate-600';
}

function formatLastSeen(ts) {
  if (!ts) return 'Offline';
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Last seen just now';
  if (diff < 3600000) return `Last seen ${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `Last seen today at ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  return `Last seen ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
}

export default function ChatWindow({ token, apiUrl, socket, friend, user, onOpenSidebar, presence }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [stagedFile, setStagedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileError, setFileError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [oldestId, setOldestId] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMsg, setEditingMsg] = useState(null);

  // New feature state
  const [friendTyping, setFriendTyping] = useState(false);
  const [friendLastSeen, setFriendLastSeen] = useState(null);
  const [reactions, setReactions] = useState({}); // { messageId: [{user_id, username, emoji}] }
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const scrollAreaRef = useRef(null);
  const contentRef = useRef(null);
  const messageEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const typingTimerRef = useRef(null);
  const isTypingRef = useRef(false);
  const _swipeStartX = useRef(null);
  // Scroll bookkeeping: whether the user is pinned to the bottom, whether the
  // next messages update is a prepend (load-earlier) that must keep position,
  // and whether the initial bottom-jump for the current chat has happened yet.
  const stickToBottomRef = useRef(true);
  const prependHeightRef = useRef(null);
  const initialScrollDoneRef = useRef(false);
  const PAGE_SIZE = 50;

  // Load messages
  useEffect(() => {
    if (!friend) { setMessages([]); return; }
    setEmojiOpen(false);
    stickToBottomRef.current = true;
    initialScrollDoneRef.current = false;
    prependHeightRef.current = null;
    setHasMore(false); setOldestId(null);
    setSearchOpen(false); setSearchQuery(''); setSearchResults([]);

    fetch(`${apiUrl}/messages/${friend.id}?limit=${PAGE_SIZE}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.json()).then((rows) => {
      setMessages(rows);
      if (rows.length === PAGE_SIZE) { setHasMore(true); setOldestId(rows[0]?.id); }
    });

    // Load reactions
    fetch(`${apiUrl}/messages/${friend.id}/reactions`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.json()).then(setReactions).catch(() => {});

    // Load last seen
    fetch(`${apiUrl}/users/${friend.id}/last-seen`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => setFriendLastSeen(d.last_seen)).catch(() => {});

    // Stop typing to this friend if we navigate away mid-type
    return () => {
      clearTimeout(typingTimerRef.current);
      if (isTypingRef.current && socket) {
        isTypingRef.current = false;
        socket.emit('typing_stop', { friendId: friend.id });
      }
    };
  }, [friend, apiUrl, token, socket]);

  // Join room
  useEffect(() => {
    if (!socket || !friend) return;
    socket.emit('join', { friendId: friend.id });
    socket.emit('mark_read', { friendId: friend.id });
  }, [socket, friend]);

  // Reconnect and refresh messages when app comes back from background
  useEffect(() => {
    if (!socket || !friend) return;
    const refreshAndRejoin = () => {
      fetch(`${apiUrl}/messages/${friend.id}?limit=${PAGE_SIZE}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => r.json()).then((rows) => {
        setMessages(rows);
        if (rows.length === PAGE_SIZE) { setHasMore(true); setOldestId(rows[0]?.id); }
        socket.emit('join', { friendId: friend.id });
        socket.emit('mark_read', { friendId: friend.id });
      });
    };
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (!socket.connected) {
        socket.once('connect', refreshAndRejoin);
        socket.connect();
      } else {
        refreshAndRejoin();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    // Also catch up if the socket reconnects on its own (e.g. a brief wifi
    // drop) while the tab stays visible the whole time — otherwise messages
    // sent by the friend during the outage would never be fetched.
    socket.on('connect', refreshAndRejoin);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      socket.off('connect', refreshAndRejoin);
    };
  }, [socket, friend, apiUrl, token]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    const onMsg = (message) => {
      setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
      // Mark as read immediately if this chat is open
      if (friend && (message.sender_id === friend.id || message.receiver_id === friend.id)) {
        socket.emit('mark_read', { friendId: friend.id });
      }
    };
    const onEdited = ({ id, content, edited }) =>
      setMessages((prev) => prev.map((m) => m.id === id ? { ...m, content, edited } : m));
    const onDeleted = ({ id }) =>
      setMessages((prev) => prev.map((m) => m.id === id ? { ...m, deleted: true, content: null } : m));
    const onTyping = ({ username, typing }) => {
      if (friend && username === friend.username) setFriendTyping(typing);
    };
    const onRead = ({ by }) => {
      if (friend && by === friend.id) {
        setMessages((prev) => prev.map((m) =>
          m.sender_id === user?.id && !m.read_at ? { ...m, read_at: new Date().toISOString() } : m
        ));
      }
    };
    const onReaction = ({ messageId, userId, username, emoji }) => {
      setReactions((prev) => {
        const cur = (prev[messageId] || []).filter((r) => r.user_id !== userId);
        if (emoji) cur.push({ user_id: userId, username, emoji });
        return { ...prev, [messageId]: cur };
      });
    };

    socket.on('message', onMsg);
    socket.on('message_edited', onEdited);
    socket.on('message_deleted', onDeleted);
    socket.on('typing', onTyping);
    socket.on('messages_read', onRead);
    socket.on('reaction', onReaction);
    return () => {
      socket.off('message', onMsg);
      socket.off('message_edited', onEdited);
      socket.off('message_deleted', onDeleted);
      socket.off('typing', onTyping);
      socket.off('messages_read', onRead);
      socket.off('reaction', onReaction);
    };
  }, [socket, friend, user]);

  // Update last seen when friend goes offline
  useEffect(() => {
    if (!friend) return;
    const isOnline = presence?.some((p) => p.id === friend.id);
    if (!isOnline) {
      fetch(`${apiUrl}/users/${friend.id}/last-seen`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json()).then((d) => setFriendLastSeen(d.last_seen)).catch(() => {});
    }
  }, [presence, friend, apiUrl, token]);

  // Scroll handling. useLayoutEffect so position is set before the browser
  // paints — no visible jump in any of the three cases.
  useLayoutEffect(() => {
    const el = scrollAreaRef.current;
    if (!el || searchOpen) return;

    // Older messages were prepended: keep the viewport anchored where it was.
    if (prependHeightRef.current !== null) {
      el.scrollTop = el.scrollHeight - prependHeightRef.current;
      prependHeightRef.current = null;
      return;
    }

    // First render of this chat: jump straight to the newest message.
    if (!initialScrollDoneRef.current) {
      el.scrollTop = el.scrollHeight;
      if (messages.length > 0) initialScrollDoneRef.current = true;
      return;
    }

    // New message arrived: only follow it if the user is already near the
    // bottom — don't yank them down while they're reading history.
    if (stickToBottomRef.current) {
      messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, searchOpen]);

  const handleScrollAreaScroll = () => {
    const el = scrollAreaRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  // Keep the view pinned to the bottom when content grows while we're there —
  // covers images finishing loading and the typing indicator appearing.
  useEffect(() => {
    const el = scrollAreaRef.current;
    const content = contentRef.current;
    if (!el || !content || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (stickToBottomRef.current && prependHeightRef.current === null) {
        el.scrollTop = el.scrollHeight;
      }
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [friend]);

  useEffect(() => {
    if (editingMsg) { setDraft(editingMsg.content || ''); textareaRef.current?.focus(); }
  }, [editingMsg]);

  const loadMore = async () => {
    if (!friend || !oldestId || loadingMore) return;
    setLoadingMore(true);
    const prevScrollHeight = scrollAreaRef.current?.scrollHeight || 0;
    const res = await fetch(`${apiUrl}/messages/${friend.id}?limit=${PAGE_SIZE}&before_id=${oldestId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const older = await res.json();
    if (older.length > 0) {
      prependHeightRef.current = prevScrollHeight - (scrollAreaRef.current?.scrollTop || 0);
      setMessages((prev) => [...older, ...prev]);
    }
    if (older.length === PAGE_SIZE) setOldestId(older[0]?.id); else setHasMore(false);
    setLoadingMore(false);
  };

  const insertEmoji = (emoji) => {
    const ta = textareaRef.current;
    setDraft((prev) => {
      const start = ta?.selectionStart ?? prev.length;
      const end = ta?.selectionEnd ?? prev.length;
      requestAnimationFrame(() => {
        if (ta) {
          ta.focus();
          ta.selectionStart = ta.selectionEnd = start + emoji.length;
        }
      });
      return prev.slice(0, start) + emoji + prev.slice(end);
    });
  };

  // Typing indicator logic
  const handleDraftChange = (e) => {
    setDraft(e.target.value);
    if (!socket || !friend) return;
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      socket.emit('typing_start', { friendId: friend.id });
    }
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
      socket.emit('typing_stop', { friendId: friend.id });
    }, 1500);
  };

  const stopTyping = () => {
    clearTimeout(typingTimerRef.current);
    if (isTypingRef.current && socket && friend) {
      isTypingRef.current = false;
      socket.emit('typing_stop', { friendId: friend.id });
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileError('');
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setFileError(`File too large. Maximum is ${MAX_FILE_MB}MB.`);
      e.target.value = ''; return;
    }
    setStagedFile(file); e.target.value = '';
  };

  const uploadFile = useCallback(async (file) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${apiUrl}/uploads`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
        else reject(new Error(JSON.parse(xhr.responseText)?.error || 'Upload failed'));
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(formData);
    });
  }, [apiUrl, token]);

  const cancelEdit = () => { setEditingMsg(null); setDraft(''); };

  const sendMessage = async () => {
    if (!friend || !socket) return;
    setFileError('');
    stopTyping();

    if (editingMsg) {
      if (!draft.trim()) return;
      socket.emit('edit_message', { messageId: editingMsg.id, content: draft.trim(), receiverId: friend.id });
      setEditingMsg(null); setDraft(''); return;
    }

    const ackMessage = (row) => {
      if (!row) return;
      setMessages((prev) => (prev.some((m) => m.id === row.id) ? prev : [...prev, row]));
    };

    if (stagedFile) {
      setUploading(true); setUploadProgress(0);
      try {
        const result = await uploadFile(stagedFile);
        socket.emit('message', {
          receiverId: friend.id, messageType: 'file',
          fileUrl: result.url, fileName: result.fileName,
          fileSize: result.fileSize, fileMime: result.fileMime,
          content: draft.trim() || null, replyToId: replyTo?.id || null,
        }, ackMessage);
        setStagedFile(null); setDraft(''); setReplyTo(null);
      } catch (err) { setFileError(err.message); }
      finally { setUploading(false); setUploadProgress(0); }
      return;
    }

    if (!draft.trim()) return;
    socket.emit('message', {
      receiverId: friend.id, messageType: 'text',
      content: draft.trim(), replyToId: replyTo?.id || null,
    }, ackMessage);
    setDraft(''); setReplyTo(null);
  };

  const handleDelete = (msg) => {
    if (!window.confirm('Delete this message for everyone?')) return;
    socket.emit('delete_message', { messageId: msg.id, receiverId: friend.id });
  };

  const handleScrollToMessage = (messageId) => {
    const el = document.getElementById(`msg-${messageId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('message-pop');
    el.addEventListener('animationend', () => el.classList.remove('message-pop'), { once: true });
  };

  const handleReact = (messageId, emoji) => {
    if (!socket || !friend) return;
    socket.emit('react', { messageId, emoji, friendId: friend.id });
  };

  const clearChat = async () => {
    if (!friend || !window.confirm(`Clear your chat history with ${friend.username}?`)) return;
    setClearing(true);
    await fetch(`${apiUrl}/messages/${friend.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setMessages([]); setHasMore(false); setOldestId(null);
    setClearing(false);
  };

  const doSearch = async (q) => {
    setSearchQuery(q);
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    const res = await fetch(`${apiUrl}/messages/${friend.id}/search?q=${encodeURIComponent(q)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setSearchResults(await res.json());
    setSearching(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    if (e.key === 'Escape') { cancelEdit(); setReplyTo(null); }
  };

  const isOnline = presence?.some((p) => p.id === friend?.id);

  if (!friend) {
    return (
      <div
        className="flex flex-1 flex-col bg-[#0d1117]"
        onTouchStart={(e) => { _swipeStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          const dx = e.changedTouches[0].clientX - (_swipeStartX.current || 0);
          if (dx > 60) onOpenSidebar();
        }}
      />
    );
  }

  return (
    <div
      className="flex flex-1 flex-col bg-[#0d1117]"
      style={{ minHeight: 0, overflow: 'hidden', height: '100%' }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-white/5 bg-[#111827] px-4 flex-shrink-0"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 10px)', paddingBottom: '10px' }}>
        <button onClick={onOpenSidebar} className="rounded-full p-2 text-slate-400 hover:bg-white/10 hover:text-white transition lg:hidden">
          <HamburgerIcon />
        </button>
        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${avatarColor(friend.username)}`}>
          {friend.username.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-white truncate">{friend.username}</p>
          <p className="text-xs leading-tight">
            {friendTyping ? (
              <span className="text-indigo-400 animate-pulse">typing…</span>
            ) : isOnline ? (
              <span className="text-emerald-400">Active now</span>
            ) : (
              <span className="text-slate-500">{formatLastSeen(friendLastSeen)}</span>
            )}
          </p>
        </div>
        {/* Search toggle */}
        <button onClick={() => { setSearchOpen((o) => !o); setSearchQuery(''); setSearchResults([]); }}
          title="Search messages"
          className={`rounded-full p-2 transition ${searchOpen ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-white/10 hover:text-white'}`}>
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 15.803a7.5 7.5 0 0010.607 10.607z" />
          </svg>
        </button>
        <button onClick={clearChat} disabled={clearing} title="Clear chat"
          className="rounded-full p-2 text-slate-400 hover:bg-white/10 hover:text-red-400 transition disabled:opacity-40">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
      </div>

      {/* Search bar */}
      {searchOpen && (
        <div className="border-b border-white/5 bg-[#111827] px-3 pb-3">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 15.803a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              autoFocus
              value={searchQuery}
              onChange={(e) => doSearch(e.target.value)}
              placeholder="Search messages…"
              className="w-full rounded-xl border border-white/10 bg-white/5 pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:border-indigo-500/50 focus:outline-none"
            />
          </div>
          {searchQuery && (
            <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
              {searching && <p className="text-xs text-slate-500 px-1">Searching…</p>}
              {!searching && searchResults.length === 0 && <p className="text-xs text-slate-500 px-1">No results found</p>}
              {searchResults.map((r) => (
                <div key={r.id} className="rounded-xl bg-white/5 px-3 py-2">
                  <p className="text-xs text-slate-400 mb-0.5">
                    {r.sender_id === user?.id ? 'You' : friend.username} · {new Date(r.created_at).toLocaleDateString()}
                  </p>
                  <p className="text-sm text-slate-200 break-words">
                    {r.content.split(new RegExp(`(${searchQuery})`, 'gi')).map((part, i) =>
                      part.toLowerCase() === searchQuery.toLowerCase()
                        ? <mark key={i} className="bg-indigo-500/40 text-white rounded px-0.5">{part}</mark>
                        : part
                    )}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollAreaRef} onScroll={handleScrollAreaScroll} className="flex-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
        <div ref={contentRef} className="px-3 py-4 space-y-1">
        {hasMore && (
          <div className="mb-4 flex justify-center">
            <button onClick={loadMore} disabled={loadingMore}
              className="rounded-full bg-white/10 px-4 py-1.5 text-xs text-slate-300 hover:bg-white/20 disabled:opacity-50 transition">
              {loadingMore ? 'Loading…' : 'Load earlier messages'}
            </button>
          </div>
        )}
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            isOwn={msg.sender_id === user?.id}
            senderName={msg.sender_id === user?.id ? user.username : friend.username}
            friendName={friend.username}
            apiUrl={apiUrl}
            token={token}
            reactions={reactions[msg.id] || []}
            currentUserId={user?.id}
            onReply={(m) => { setReplyTo(m); setEditingMsg(null); textareaRef.current?.focus(); }}
            onEdit={(m) => { setEditingMsg(m); setReplyTo(null); }}
            onDelete={handleDelete}
            onReact={handleReact}
            onScrollToMessage={handleScrollToMessage}
          />
        ))}

        {/* Typing indicator bubble */}
        {friendTyping && (
          <div className="flex items-end gap-2">
            <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${avatarColor(friend.username)}`}>
              {friend.username.slice(0, 2).toUpperCase()}
            </div>
            <div className="rounded-2xl rounded-bl-none bg-[#273047] px-4 py-3">
              <span className="flex gap-1">
                <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="h-2 w-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </span>
            </div>
          </div>
        )}

          <div ref={messageEndRef} />
        </div>
      </div>

      {/* Input bar */}
      <div className="relative border-t border-white/5 bg-[#111827] px-3 flex-shrink-0"
        style={{ paddingTop: '12px', paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}>
        {emojiOpen && <EmojiPicker onPick={insertEmoji} onClose={() => setEmojiOpen(false)} />}
        {fileError && <p className="mb-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{fileError}</p>}

        {editingMsg && (
          <div className="mb-2 flex items-center gap-2 rounded-xl border border-indigo-500/30 bg-indigo-500/10 px-3 py-2">
            <svg className="h-4 w-4 flex-shrink-0 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-indigo-400">Editing message</p>
              <p className="text-xs text-slate-400 truncate">{editingMsg.content}</p>
            </div>
            <button onClick={cancelEdit} className="flex-shrink-0 rounded-full p-1 text-slate-400 hover:bg-white/10 hover:text-white">
              <XIcon />
            </button>
          </div>
        )}

        {replyTo && !editingMsg && (
          <div className="mb-2 flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
            <svg className="h-4 w-4 flex-shrink-0 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-indigo-400">{replyTo.sender_id === user?.id ? 'You' : friend.username}</p>
              <p className="text-xs text-slate-400 truncate">
                {replyTo.deleted ? 'Deleted message' : replyTo.message_type === 'file' ? `📎 ${replyTo.file_name || 'File'}` : replyTo.content}
              </p>
            </div>
            <button onClick={() => setReplyTo(null)} className="flex-shrink-0 rounded-full p-1 text-slate-400 hover:bg-white/10 hover:text-white">
              <XIcon />
            </button>
          </div>
        )}

        {stagedFile && (
          <div className="mb-2 flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm">
            <svg className="h-4 w-4 flex-shrink-0 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
            </svg>
            <span className="flex-1 truncate text-slate-300 text-sm">{stagedFile.name}</span>
            <button onClick={() => setStagedFile(null)} className="flex-shrink-0 rounded-full p-1 text-slate-400 hover:bg-white/10 hover:text-white"><XIcon /></button>
          </div>
        )}

        {uploading && (
          <div className="mb-2">
            <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
              <div className="h-1 rounded-full bg-indigo-500 transition-all duration-200" style={{ width: `${uploadProgress}%` }} />
            </div>
            <p className="mt-1 text-right text-xs text-slate-500">{uploadProgress}%</p>
          </div>
        )}

        <div className="flex items-end gap-2">
          {!editingMsg && (
            <label className="flex-shrink-0 cursor-pointer rounded-full p-2.5 text-slate-400 hover:bg-white/10 hover:text-slate-200 transition">
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect}
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.7z" />
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
              </svg>
            </label>
          )}
          <button
            onClick={() => setEmojiOpen((v) => !v)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="Emoji picker"
            className={`flex-shrink-0 rounded-full p-2.5 transition hover:bg-white/10
              ${emojiOpen ? 'bg-white/10 text-indigo-400' : 'text-slate-400 hover:text-slate-200'}`}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
            </svg>
          </button>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={handleDraftChange}
            onKeyDown={handleKeyDown}
            placeholder={editingMsg ? 'Edit message…' : stagedFile ? 'Add a caption…' : 'Message…'}
            rows={1}
            disabled={uploading}
            id="chat-input"
            autoComplete="off"
            autoCorrect="on"
            autoCapitalize="sentences"
            spellCheck="true"
            enterKeyHint="send"
            inputMode="text"
            className="flex-1 resize-none rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-indigo-500/50 focus:outline-none transition disabled:opacity-50"
            style={{ maxHeight: '120px', overflowY: 'auto' }}
          />
          <button onClick={sendMessage} disabled={uploading || (!draft.trim() && !stagedFile)}
            className="flex-shrink-0 rounded-full bg-indigo-600 p-2.5 text-white transition hover:bg-indigo-500 disabled:opacity-30 disabled:cursor-default">
            {editingMsg ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function HamburgerIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
